import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'

// サンプル患者データ
const samplePatients = [
  { id: 'pat-001', researchId: 'R01166', name: 'KuKn', hospitalId: 'hosp-001' },
  { id: 'pat-002', researchId: 'R01598', name: 'ItuHrs', hospitalId: 'hosp-004' },
  { id: 'pat-003', researchId: 'R00500', name: 'SymTsk', hospitalId: 'hosp-005' },
  { id: 'pat-004', researchId: 'R01594', name: 'FmtHrv', hospitalId: 'hosp-006' },
  { id: 'pat-005', researchId: 'R01645', name: 'YsmrYuk', hospitalId: 'hosp-007' },
  { id: 'pat-006', researchId: 'R00483', name: 'AihrChr', hospitalId: 'hosp-003' },
  { id: 'pat-008', researchId: 'R01701', name: 'YmdTky', hospitalId: 'hosp-001' },
  { id: 'pat-009', researchId: 'R01702', name: 'TnkRk', hospitalId: 'hosp-003' },
]

// サンプル病院データ
const sampleHospitals = [
  { id: 'hosp-001', name: 'Baseクリニック赤坂' },
  { id: 'hosp-002', name: 'サンテクリニック' },
  { id: 'hosp-003', name: '城谷バイオウェルネスクリニック' },
  { id: 'hosp-004', name: '二子玉川メディカルクリニック' },
  { id: 'hosp-005', name: '感謝クリニック' },
  { id: 'hosp-006', name: 'LAGOMクリニック' },
  { id: 'hosp-007', name: 'よろずクリニック' },
  { id: 'hosp-008', name: 'シンバイオシス研究所' },
]

// サンプル医師データ
const sampleDoctors = [
  { id: 'doc-001', name: '吉岡 紘平', hospitalId: 'hosp-001' },
  { id: 'doc-002', name: '山田 太郎', hospitalId: 'hosp-002' },
  { id: 'doc-003', name: '城谷 昌彦', hospitalId: 'hosp-003' },
  { id: 'doc-004', name: '佐藤 健一', hospitalId: 'hosp-004' },
  { id: 'doc-005', name: '田村 一郎', hospitalId: 'hosp-005' },
  { id: 'doc-006', name: '鈴木 花子', hospitalId: 'hosp-006' },
  { id: 'doc-007', name: '高橋 誠', hospitalId: 'hosp-007' },
  { id: 'doc-008', name: '中川 直哉', hospitalId: 'hosp-008' },
  { id: 'doc-009', name: '佐々木 美咲', hospitalId: 'hosp-001' },
  { id: 'doc-010', name: '木村 大輔', hospitalId: 'hosp-003' },
]

const timeSlots = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
]

export default function ReservationCreate() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null)

  const isAdminOrOffice = user?.role === 'admin' || user?.role === 'office'
  const userHospitalId = user?.hospitalId

  const [formData, setFormData] = useState({
    patientId: '',
    hospitalId: isAdminOrOffice ? '' : userHospitalId || '',
    doctorId: '',
    date: '',
    time: '',
    transplantNumber: 1,
    notes: '',
    status: 'pending',
  })

  // フィルタリングされた患者リスト
  const availablePatients = isAdminOrOffice 
    ? samplePatients 
    : samplePatients.filter(p => p.hospitalId === userHospitalId)

  // フィルタリングされた医師リスト
  const availableDoctors = formData.hospitalId 
    ? sampleDoctors.filter(d => d.hospitalId === formData.hospitalId)
    : []

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.patientId || !formData.date || !formData.time || !formData.hospitalId) {
      showNotification('error', '必須項目を入力してください')
      return
    }

    // 実際のシステムではここでAPIを呼び出す
    showNotification('success', '予約を作成しました')
    
    setTimeout(() => {
      navigate('/reservations')
    }, 1500)
  }

  const handleHospitalChange = (hospitalId: string) => {
    setFormData({
      ...formData,
      hospitalId,
      doctorId: '', // 病院が変わったら医師をリセット
    })
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <Link to="/reservations" className="aip-button aip-button--icon">
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <h1 className="aip-main__header-title">移植予約の作成</h1>
        </div>
      </header>

      <div className="aip-main__content">
        {/* 通知 */}
        {notification && (
          <div className={`notification notification--${notification.type}`}>
            <span className="material-symbols-outlined">
              {notification.type === 'success' ? 'check_circle' : 'error'}
            </span>
            {notification.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="create-form">
          <div className="form-section">
            <h2 className="form-section__title">予約情報</h2>
            
            <div className="form-grid">
              <div className="form-group form-group--full">
                <label className="form-label">
                  患者 <span className="required">*</span>
                </label>
                <select
                  className="form-input"
                  value={formData.patientId}
                  onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
                  required
                >
                  <option value="">患者を選択してください</option>
                  {availablePatients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.researchId} - {patient.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  移植実施病院 <span className="required">*</span>
                </label>
                <select
                  className="form-input"
                  value={formData.hospitalId}
                  onChange={(e) => handleHospitalChange(e.target.value)}
                  required
                  disabled={!isAdminOrOffice}
                >
                  <option value="">病院を選択してください</option>
                  {sampleHospitals.map((hospital) => (
                    <option key={hospital.id} value={hospital.id}>
                      {hospital.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">担当医師</label>
                <select
                  className="form-input"
                  value={formData.doctorId}
                  onChange={(e) => setFormData({ ...formData, doctorId: e.target.value })}
                  disabled={!formData.hospitalId}
                >
                  <option value="">医師を選択してください</option>
                  {availableDoctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  予約日 <span className="required">*</span>
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  予約時間 <span className="required">*</span>
                </label>
                <select
                  className="form-input"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  required
                >
                  <option value="">時間を選択してください</option>
                  {timeSlots.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">移植回数</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.transplantNumber}
                  onChange={(e) => setFormData({ ...formData, transplantNumber: parseInt(e.target.value) || 1 })}
                  min={1}
                  max={20}
                />
              </div>

              <div className="form-group">
                <label className="form-label">ステータス</label>
                <select
                  className="form-input"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="pending">受付済</option>
                  <option value="confirmed">確認済</option>
                  <option value="processing">処理中</option>
                  <option value="hold">保留中</option>
                </select>
              </div>

              <div className="form-group form-group--full">
                <label className="form-label">備考</label>
                <textarea
                  className="form-input form-textarea"
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="予約に関する備考があれば入力してください"
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <Link to="/reservations" className="aip-button aip-button--outline">
              キャンセル
            </Link>
            <button type="submit" className="aip-button aip-button--solid-fill">
              <span className="material-symbols-outlined">check</span>
              予約を作成
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .create-form {
          max-width: 800px;
        }

        .form-section {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
          padding: var(--spacing-lg);
          margin-bottom: var(--spacing-lg);
        }

        .form-section__title {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: var(--spacing-lg);
          padding-bottom: var(--spacing-sm);
          border-bottom: 1px solid var(--border-color);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--spacing-md);
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-xs);
        }

        .form-group--full {
          grid-column: 1 / -1;
        }

        .form-label {
          font-size: 0.875rem;
          font-weight: 500;
        }

        .required {
          color: var(--color-alert);
        }

        .form-input {
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          font-family: inherit;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--color-blue500);
          box-shadow: 0 0 0 3px var(--color-blue100);
        }

        .form-input:disabled {
          background-color: var(--color-solid-gray50);
          cursor: not-allowed;
        }

        .form-textarea {
          resize: vertical;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-md);
        }

        /* 通知 */
        .notification {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md);
          border-radius: var(--border-radius-md);
          margin-bottom: var(--spacing-md);
          animation: slideIn 0.3s ease;
        }

        .notification--success {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .notification--error {
          background-color: var(--color-alert-light);
          color: var(--color-alert);
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  )
}



